import React, {useState, useEffect, useLayoutEffect, useRef} from 'react';

import Logo from './../../assets/image/logo.png';
import SocialLink from '../../components/SocialLink/SocialLink.js';
import {Row,Col} from 'react-bootstrap';

import './Header.scss'

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function getFocusableElements(root) {
    if (!root) return [];
    return Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter((el) => {
        if (el.hasAttribute('disabled')) return false;
        const style = window.getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden') return false;
        const ti = el.getAttribute('tabindex');
        if (ti !== null && parseInt(ti, 10) < 0) return false;
        return true;
    });
}

const Header = () => {
    const [menuOpen,changeMenuOpen] = useState(false);
    const menuPanelRef = useRef(null);
    const menuButtonRef = useRef(null);
    const firstMenuLinkRef = useRef(null);
    const headerContainerRef = useRef(null);
    var addedClass = '';
    if(menuOpen){
        addedClass = 'is-open';
    }
    useEffect(()=>{
        //console.log('effect');
        if(menuOpen){
            document.querySelectorAll('body')[0].classList.add('is-menu-open');
        }else{
            document.querySelectorAll('body')[0].classList.remove('is-menu-open');
        }
    },[menuOpen])

    useLayoutEffect(() => {
        const el = menuPanelRef.current;
        if (!el) return;
        if (menuOpen) {
            el.removeAttribute('inert');
            el.removeAttribute('aria-hidden');
        } else {
            el.setAttribute('inert', '');
            el.setAttribute('aria-hidden', 'true');
        }
    }, [menuOpen]);

    useLayoutEffect(() => {
        const container = headerContainerRef.current;
        if (!container || !menuOpen) return undefined;
        const links = Array.from(container.querySelectorAll('a'));
        const stored = links.map((a) => ({ el: a, prev: a.getAttribute('tabindex') }));
        links.forEach((a) => {
            a.tabIndex = -1;
        });
        return () => {
            stored.forEach(({ el, prev }) => {
                if (prev === null) el.removeAttribute('tabindex');
                else el.setAttribute('tabindex', prev);
            });
        };
    }, [menuOpen]);

    useEffect(() => {
        if (!menuOpen) return;
        const id = window.requestAnimationFrame(() => {
            firstMenuLinkRef.current?.focus();
        });
        return () => window.cancelAnimationFrame(id);
    }, [menuOpen]);

    useEffect(() => {
        if (!menuOpen) return;
        let trapActive = true;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                changeMenuOpen(false);
                menuButtonRef.current?.focus();
                return;
            }
            if (e.key !== 'Tab') return;
            const nav = menuPanelRef.current;
            const btn = menuButtonRef.current;
            if (!nav || !btn) return;
            const focusables = getFocusableElements(nav);
            if (focusables.length === 0) return;
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            const active = document.activeElement;
            if (e.shiftKey) {
                if (active === first) {
                    e.preventDefault();
                    btn.focus();
                } else if (active === btn) {
                    e.preventDefault();
                    last.focus();
                }
            } else if (active === last) {
                e.preventDefault();
                btn.focus();
            } else if (active === btn) {
                e.preventDefault();
                first.focus();
            }
        };
        const onFocusIn = (e) => {
            const nav = menuPanelRef.current;
            const btn = menuButtonRef.current;
            if (!nav || !btn) return;
            const target = e.target;
            if (nav.contains(target) || target === btn) return;
            const focusables = getFocusableElements(nav);
            const fallback = focusables.length > 0 ? focusables[0] : btn;
            queueMicrotask(() => {
                if (!trapActive) return;
                fallback.focus();
            });
        };
        document.addEventListener('keydown', onKeyDown, true);
        document.addEventListener('focusin', onFocusIn, true);
        return () => {
            trapActive = false;
            document.removeEventListener('keydown', onKeyDown, true);
            document.removeEventListener('focusin', onFocusIn, true);
        };
    }, [menuOpen]);
    
    return (<header className={"Header "+addedClass}>
        <div className="header-container pt-md-0 pt-3" ref={headerContainerRef}>
            <Row className="align-items-start">
                <Col><a href="https://www.fetenationale.gouv.qc.ca/" loading="lazy" aria-label="Mouvement national des Québecois"><img className="logo" src={Logo} alt="Mouvement National des Québecois" /></a></Col>
                <Col className="mainmenu ">
                    <div className="social d-none d-md-block">
                        <SocialLink variant="facebook" />
                        <SocialLink variant="instagram" />
                        <SocialLink variant="x" />
                        <SocialLink variant="youtube" />
                    </div>
                    <button
                        type="button"
                        className="opener"
                        ref={menuButtonRef}
                        onClick={() => { changeMenuOpen(!menuOpen); }}
                        aria-expanded={menuOpen}
                        aria-controls="main-navigation"
                        aria-label={menuOpen ? 'Fermer le menu principal' : 'Ouvrir le menu principal'}
                    >
                        <span aria-hidden="true" />
                    </button>
                </Col>
            </Row>
        </div>
        <nav
            className="headingMenu"
            ref={menuPanelRef}
            id="main-navigation"
            aria-label="Menu principal"
        >
            <div>
                <Row>
                    <Col className="">
                        <ul className="HeaderMainMenu">
                            <li><a ref={firstMenuLinkRef} href="https://fetenationale.gouv.qc.ca/capitale-nationale/">Fête nationale du Québec dans la capitale</a></li>
                            <li><a href="https://fetenationale.gouv.qc.ca/montreal/">Fête nationale du Québec à Montréal</a></li>
                            <li><a href="/">Calendrier national des festivités</a></li>
                        </ul>
                    </Col>
                    <Col>
                        <ul className="HeaderSecMenu mb-8">
                            <li><a href="https://fetenationale.gouv.qc.ca/nouvelles/">Actualités</a></li>
                            <li><a href="https://fetenationale.gouv.qc.ca/porte-parole/">Porte-parole</a></li>
                            <li><a href="https://fetenationale.gouv.qc.ca/theme-commemoratif-2026/">Thème commémoratif</a></li>
                            <li><a href="https://fetenationale.gouv.qc.ca/partenaires/">Partenaires</a></li>
                            <li><a href="https://fetenationale.gouv.qc.ca/nous-joindre/">Nous joindre</a></li>
                        </ul>
                        <div>
                            <SocialLink variant="facebook" />
                            <SocialLink variant="instagram" />
                            <SocialLink variant="x" />
                            <SocialLink variant="youtube" />
                        </div>
                    </Col>
                </Row>
            </div>
        </nav>
    </header>);
}
export default Header;